import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { api } from '@/api/client';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState, Spinner } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import {
  beaconUploadFailed,
  getJobStatus,
  getUploadStatus,
  presignMissingUploads,
  reportUploadFailed,
  retryJob,
  sendUploadHeartbeat,
  shouldUseMultipartUpload,
  startPipeline,
  uploadAudioToS3,
  uploadAudioMultipartToS3,
} from '@/api/pipeline';
import type { PipelineJobResponse, PipelineStatus, PipelineUploadStatusResponse } from '@/types/pipeline';
import { useAuthStore } from '@/state/authStore';
import { formatMeetingType } from '@/utils/meetingType';
import { mapWithConcurrency } from '@/utils/async';
import { guessUploadContentType } from '@/utils/uploadContentType';

const STEPS: { status: SessionStatus; label: string }[] = [
  { status: 'transcribing', label: '文字起こし中' },
  { status: 'section_config_pending', label: 'セクション設定中' },
  { status: 'ai_evaluating', label: 'AI評価中' },
  { status: 'feedback_pending', label: 'フィードバック中' },
];
const STEP_ORDER: Record<string, number> = {
  transcribing: 0, transcribing_failed: 0, section_config_pending: 1,
  ai_evaluating: 2, ai_evaluation_failed: 2,
  feedback_pending: 3, feedback_in_progress: 3, coach_ack_pending: 4, completed: 4,
};

const PIPELINE_STEPS: { statuses: PipelineStatus[]; label: string }[] = [
  { statuses: ['pending', 'transcribing'], label: '文字起こし' },
  { statuses: ['correcting'], label: 'Gemini 補正' },
  { statuses: ['section_pending'], label: 'セクション設定' },
  { statuses: ['evaluating'], label: 'AI 評価' },
  { statuses: ['completed'], label: '完了' },
];

const PIPELINE_FAIL_STATUSES: PipelineStatus[] = ['failed'];
const S3_UPLOAD_CONCURRENCY = 1;
const S3_UPLOAD_MAX_ATTEMPTS = 2;
const S3_UPLOAD_STALL_TIMEOUT_MS = 180_000;

function pipelineStepIndex(status: PipelineStatus): number {
  for (let i = 0; i < PIPELINE_STEPS.length; i++) {
    if (PIPELINE_STEPS[i]?.statuses.includes(status)) return i;
  }
  return 0;
}

export function reconcilePipelineJobWithSession(
  job: PipelineJobResponse,
  session: Record<string, unknown> | null,
): PipelineJobResponse {
  const sessionStatus = session?.status;
  if (
    sessionStatus === 'ai_evaluating' &&
    (job.status === 'correcting' || job.status === 'section_pending')
  ) {
    return {
      ...job,
      status: 'evaluating',
      progress: {
        stage: 'evaluating',
        detail: 'セクション確定、AI評価を開始しています。',
      },
    };
  }
  return job;
}

function errorText(err: unknown, fallback: string): string {
  const detail =
    (err as { response?: { data?: { detail?: string | { message?: string }; message?: string } } })
      .response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return (
    (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

function uploadItemKey(role: string, filename: string): string {
  return `${role}/${filename}`;
}

function createUploadHeartbeatReporter(jobId: string) {
  let lastSentAt = 0;
  return (force = false) => {
    const now = Date.now();
    if (!force && now - lastSentAt < 10_000) return;
    lastSentAt = now;
    void sendUploadHeartbeat(jobId).catch(() => undefined);
  };
}

async function uploadFileWithRetry(
  jobId: string,
  role: string,
  filename: string,
  uploadUrl: string,
  file: File,
  onProgress: (percent: number) => void,
  contentType?: string,
  signal?: AbortSignal,
) {
  if (shouldUseMultipartUpload(file)) {
    await uploadAudioMultipartToS3(
      jobId,
      {
        role,
        filename,
        content_type: contentType || guessUploadContentType(file),
        file_size: file.size,
      },
      file,
      {
        onProgress,
        stallTimeoutMs: S3_UPLOAD_STALL_TIMEOUT_MS,
        signal,
      },
    );
    return;
  }
  let lastError: unknown;
  for (let attempt = 1; attempt <= S3_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
    try {
      await uploadAudioToS3(uploadUrl, file, onProgress, contentType, {
        stallTimeoutMs: S3_UPLOAD_STALL_TIMEOUT_MS,
        signal,
      });
      return;
    } catch (err) {
      lastError = err;
      if (attempt < S3_UPLOAD_MAX_ATTEMPTS) {
        await new Promise((resolve) => window.setTimeout(resolve, attempt * 1000));
      }
    }
  }
  throw lastError;
}

function statusDetailLabel(statusDetail: unknown): string | undefined {
  if (statusDetail === 'uploading') return '音声アップロード中';
  if (statusDetail === 'upload_failed') return '音声アップロード失敗';
  if (statusDetail === 'start_pending') return '処理開始待ち';
  return undefined;
}

function statusDetailClass(statusDetail: unknown): string | undefined {
  if (statusDetail === 'uploading') return 'bg-blue-100 text-blue-800';
  if (statusDetail === 'upload_failed') return 'bg-red-100 text-red-800';
  if (statusDetail === 'start_pending') return 'bg-yellow-100 text-yellow-800';
  return undefined;
}

export function ProcessingStatusPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.currentUser);
  const jobIdQuery = searchParams.get('jobId');
  const isPipelineMode = Boolean(jobIdQuery);

  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [pipelineJob, setPipelineJob] = useState<PipelineJobResponse | null>(null);
  const [uploadStatus, setUploadStatus] = useState<PipelineUploadStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [refreshSeq, setRefreshSeq] = useState(0);
  const pollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce() {
      try {
        if (isPipelineMode && jobIdQuery) {
          const job = await getJobStatus(jobIdQuery);
          const uploads =
            job.status === 'pending' || job.status === 'failed'
              ? await getUploadStatus(jobIdQuery)
              : null;
          const sessionResp = sessionId ? await api.get(`/sessions/${sessionId}`) : null;
          if (cancelled) return;
          const sessionData = sessionResp?.data ?? null;
          setPipelineJob(reconcilePipelineJobWithSession(job, sessionData));
          setUploadStatus(uploads);
          if (sessionResp) setSession(sessionData);
          setError(null);
          // ポーリング: 完了/失敗以外は 5 秒間隔
          const isTerminal =
            job.status === 'completed' || PIPELINE_FAIL_STATUSES.includes(job.status);
          if (!isTerminal) {
            pollTimerRef.current = window.setTimeout(fetchOnce, 5000);
          }
        } else if (sessionId) {
          const r = await api.get(`/sessions/${sessionId}`);
          if (cancelled) return;
          setSession(r.data);
          setUploadStatus(null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          // @ts-expect-error - userMessage attached by axios interceptor
          err?.userMessage || (err instanceof Error ? err.message : 'データ取得に失敗しました');
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    fetchOnce();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [sessionId, jobIdQuery, isPipelineMode, refreshSeq]);

  const canRetry = Boolean(
    session &&
      (user?.is_admin || String(session.coach_user_id) === String(user?.id)),
  );

  async function handleRetry(jobId: string) {
    setRetrying(true);
    setError(null);
    try {
      await retryJob(jobId);
      if (!isPipelineMode && sessionId) {
        navigate(`/sessions/${sessionId}/status?jobId=${encodeURIComponent(jobId)}`);
      }
      setRefreshSeq(v => v + 1);
    } catch (err) {
      const msg =
        // @ts-expect-error - userMessage attached by axios interceptor
        err?.userMessage || (err instanceof Error ? err.message : '再試行に失敗しました');
      setError(msg);
    } finally {
      setRetrying(false);
    }
  }

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          <p className="font-semibold">処理状況の取得に失敗しました</p>
          <p className="mt-2">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  if (isPipelineMode && pipelineJob) {
    return (
      <PipelineStatusView
        job={pipelineJob}
        sessionId={sessionId}
        uploadStatus={uploadStatus}
        canRetry={canRetry}
        retrying={retrying}
        onRetry={handleRetry}
        onRecovered={() => setRefreshSeq(v => v + 1)}
      />
    );
  }

  if (!session) return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  const status = session.status as SessionStatus;
  const currentStep = STEP_ORDER[status] ?? 0;
  const failed = status === 'transcribing_failed' || status === 'ai_evaluation_failed';

  return (
    <div>
      <div className="mb-4"><Link to="/sessions" className="text-sm text-blue-600 hover:underline">← セッション一覧</Link></div>
      <h1 className="text-xl font-semibold text-gray-900">処理状況</h1>
      <p className="mt-1 text-sm text-gray-500">
        {session.client_name as string} / {session.coach_name as string} — {formatMeetingType(session.day_type as string)} #{session.serial_no as number}
      </p>
      <div className="mt-6 rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {STEPS.map((step, i) => (
            <div key={step.status} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                  i < currentStep ? 'bg-green-500 text-white'
                    : i === currentStep && failed ? 'bg-red-500 text-white'
                    : i === currentStep ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-500'}`}>
                  {i < currentStep ? '✓' : i === currentStep && failed ? '!' : i + 1}
                </div>
                <p className={`mt-2 text-xs ${i <= currentStep ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
              </div>
              {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${i < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">現在のステータス:</span>
            <StatusBadge
              status={status}
              labelOverride={statusDetailLabel(session.status_detail)}
              classNameOverride={statusDetailClass(session.status_detail)}
            />
          </div>
          {failed && canRetry && (
            <button
              type="button"
              onClick={() => handleRetry(sessionId || String(session.id))}
              disabled={retrying}
              className="rounded bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {retrying ? '再試行中...' : '再試行'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PipelineStatusView({
  job,
  sessionId,
  uploadStatus,
  canRetry,
  retrying,
  onRetry,
  onRecovered,
}: {
  job: PipelineJobResponse;
  sessionId?: string;
  uploadStatus: PipelineUploadStatusResponse | null;
  canRetry: boolean;
  retrying: boolean;
  onRetry: (jobId: string) => void;
  onRecovered: () => void;
}) {
  const status = job.status;
  const failed = PIPELINE_FAIL_STATUSES.includes(status);
  const isCompleted = status === 'completed';
  const isSectionPending = status === 'section_pending';
  const currentStep = pipelineStepIndex(status);
  const sectionConfigUrl = sessionId
    ? `/sessions/${sessionId}/sections?jobId=${encodeURIComponent(job.job_id)}${job.day ? `&day=${encodeURIComponent(job.day)}` : ''}`
    : null;
  const missingUploads = useMemo(
    () => uploadStatus?.items.filter((item) => !item.uploaded) ?? [],
    [uploadStatus?.items],
  );
  const uploadedItems = useMemo(
    () => uploadStatus?.items.filter((item) => item.uploaded) ?? [],
    [uploadStatus?.items],
  );
  const canRecoverUpload =
    Boolean(uploadStatus) &&
    (status === 'pending' || uploadStatus?.upload_state === 'upload_failed') &&
    missingUploads.length > 0;
  const isUploadIncomplete = canRecoverUpload;
  const canManualStart = status === 'pending' && uploadStatus?.complete === true;
  const uploadStateLabel = statusDetailLabel(uploadStatus?.upload_state);
  const isUploadStateFailed = uploadStatus?.upload_state === 'upload_failed';
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [recoveringUploads, setRecoveringUploads] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryCancelledRef = useRef(false);
  const recoveryAbortControllerRef = useRef<AbortController | null>(null);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!recoveringUploads) return false;
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search
    );
  });

  const missingUploadTargets = useMemo(() => missingUploads.map((item) => ({
    role: item.role,
    filename: item.filename,
  })), [missingUploads]);

  useEffect(() => () => {
    recoveryAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (!window.confirm('再アップロード中です。移動すると中断される可能性があります。移動しますか？')) {
      blocker.reset();
      return;
    }
    recoveryCancelledRef.current = true;
    recoveryAbortControllerRef.current?.abort();
    beaconUploadFailed(job.job_id, {
      reason: 'in-app navigation during audio re-upload',
      missing_files: missingUploadTargets,
    });
    setRecoveringUploads(false);
    blocker.proceed();
  }, [blocker, job.job_id, missingUploadTargets]);

  useEffect(() => {
    if (!recoveringUploads) return;
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      beaconUploadFailed(job.job_id, {
        reason: 'browser unload during audio re-upload',
        missing_files: missingUploadTargets,
      });
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [recoveringUploads, job.job_id, missingUploadTargets]);

  useEffect(() => {
    if (!recoveringUploads) return;
    void sendUploadHeartbeat(job.job_id).catch(() => undefined);
    const timer = window.setInterval(() => {
      void sendUploadHeartbeat(job.job_id).catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [recoveringUploads, job.job_id]);

  const handleRecoveryFileChange = (
    role: string,
    filename: string,
    fileList: FileList | null,
  ) => {
    const key = uploadItemKey(role, filename);
    const file = fileList?.[0];
    if (!file) return;
    if (file.name !== filename) {
      setRecoveryError(`選択されたファイル名が一致しません: ${filename}`);
      setSelectedFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setRecoveryError(null);
    setSelectedFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleRecoverUploads = async () => {
    if (missingUploads.some((item) => !selectedFiles[uploadItemKey(item.role, item.filename)])) {
      setRecoveryError('不足しているファイルをすべて選択してください。');
      return;
    }

    recoveryCancelledRef.current = false;
    setRecoveringUploads(true);
    setRecoveryError(null);
    setUploadProgress({});
    const abortController = new AbortController();
    recoveryAbortControllerRef.current = abortController;
    let uploadPhase: 'uploading' | 'starting' = 'uploading';
    try {
      await sendUploadHeartbeat(job.job_id).catch(() => undefined);
      const reportUploadHeartbeat = createUploadHeartbeatReporter(job.job_id);
      const presignResp = await presignMissingUploads(
        job.job_id,
        missingUploads.map((item) => {
          const file = selectedFiles[uploadItemKey(item.role, item.filename)]!;
          return {
            role: item.role,
            filename: item.filename,
            content_type: guessUploadContentType(file),
          };
        }),
      );

      await mapWithConcurrency(presignResp.upload_urls, S3_UPLOAD_CONCURRENCY, async (url) => {
        const key = uploadItemKey(url.role, url.filename);
        const file = selectedFiles[key];
        if (!file) return;
        await uploadFileWithRetry(
          job.job_id,
          url.role,
          url.filename,
          url.upload_url,
          file,
          (pct) => {
            setUploadProgress((prev) => ({ ...prev, [key]: pct }));
            reportUploadHeartbeat();
          },
          url.content_type ?? guessUploadContentType(file),
          abortController.signal,
        );
        reportUploadHeartbeat(true);
      });

      if (recoveryCancelledRef.current) return;
      uploadPhase = 'starting';
      await startPipeline(job.job_id);
      if (recoveryCancelledRef.current) return;
      onRecovered();
    } catch (err) {
      if (recoveryCancelledRef.current) return;
      const message = errorText(
        err,
        uploadPhase === 'starting' ? '処理開始に失敗しました' : '再アップロードに失敗しました',
      );
      if (uploadPhase === 'uploading') {
        await reportUploadFailed(job.job_id, {
          reason: message,
          missing_files: missingUploadTargets,
        }).catch(() => undefined);
      }
      setRecoveryError(message);
    } finally {
      setRecoveringUploads(false);
      if (recoveryAbortControllerRef.current === abortController) {
        recoveryAbortControllerRef.current = null;
      }
    }
  };

  const handleManualStart = async () => {
    setRecoveringUploads(true);
    setRecoveryError(null);
    try {
      await startPipeline(job.job_id);
      onRecovered();
    } catch (err) {
      setRecoveryError(errorText(err, '処理開始に失敗しました'));
    } finally {
      setRecoveringUploads(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Link to="/sessions" className="text-sm text-blue-600 hover:underline">
          ← セッション一覧
        </Link>
      </div>
      <h1 className="text-xl font-semibold text-gray-900">処理状況</h1>
      <p className="mt-1 text-sm text-gray-500">
        Job ID: <span className="font-mono">{job.job_id}</span>
        {job.day && ` — ${formatMeetingType(job.day)}`}
        {typeof job.cycle === 'number' && ` (cycle ${job.cycle})`}
      </p>

      <div className="mt-6 rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {PIPELINE_STEPS.map((step, i) => (
            <div key={step.label} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                  i < currentStep ? 'bg-green-500 text-white'
                    : i === currentStep && failed ? 'bg-red-500 text-white'
                    : i === currentStep && !isCompleted ? 'bg-blue-500 text-white'
                    : i === currentStep && isCompleted ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'}`}>
                  {i < currentStep || (i === currentStep && isCompleted) ? '✓' : i === currentStep && failed ? '!' : i + 1}
                </div>
                <p className={`mt-2 text-xs ${i <= currentStep ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
              </div>
              {i < PIPELINE_STEPS.length - 1 && (
                <div className={`mx-2 h-0.5 flex-1 ${i < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">現在のステータス:</span>
            <span className="font-mono text-sm text-gray-800">{uploadStateLabel ?? status}</span>
            {!isCompleted && !failed && !isUploadStateFailed && <Spinner size="sm" />}
          </div>
        </div>

        {job.progress?.detail && (
          <p className="mt-3 text-xs text-gray-500">{job.progress.detail}</p>
        )}

        {(status === 'pending' || canRecoverUpload) && uploadStatus && (
          <div className={`mt-4 rounded-lg border p-4 text-sm ${
            isUploadIncomplete
              ? 'border-orange-200 bg-orange-50 text-orange-900'
              : 'border-blue-200 bg-blue-50 text-blue-900'
          }`}>
            <p className="font-semibold">
              {uploadStatus.upload_state === 'upload_failed'
                ? '音声アップロード失敗'
                : uploadStatus.upload_state === 'uploading'
                  ? '音声アップロード中'
                  : isUploadIncomplete ? 'アップロード未完了' : 'アップロード完了、処理開始待ち'}
            </p>
            {isUploadIncomplete ? (
              <p className="mt-1 text-xs">
                不足しているファイルだけ選択して再アップロードできます。既にアップロード済みのファイルは再送信しません。
              </p>
            ) : (
              <p className="mt-1 text-xs">音声ファイルは揃っています。画面離脱などで開始処理だけ失敗した可能性があります。</p>
            )}

            {uploadedItems.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium">アップロード済み</p>
                <ul className="mt-1 space-y-1 text-xs">
                  {uploadedItems.map((item) => (
                    <li key={item.s3_key} className="font-mono text-green-700">
                      ✓ {uploadItemKey(item.role, item.filename)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {missingUploads.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium">不足ファイル</p>
                {missingUploads.map((item) => {
                  const key = uploadItemKey(item.role, item.filename);
                  const progress = uploadProgress[key];
                  return (
                    <div key={item.s3_key} className="rounded border border-orange-200 bg-white/70 p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span className="font-mono text-xs">{key}</span>
                        <input
                          type="file"
                          accept="audio/*,video/*,.wav,.mp3,.m4a,.flac,.mp4,.webm"
                          disabled={recoveringUploads}
                          onChange={(e) => handleRecoveryFileChange(item.role, item.filename, e.target.files)}
                          className="text-xs"
                        />
                      </div>
                      {selectedFiles[key] && (
                        <p className="mt-1 text-xs text-orange-700">選択済み: {selectedFiles[key].name}</p>
                      )}
                      {progress !== undefined && (
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-orange-100">
                          <div
                            className="h-full bg-orange-500 transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {recoveryError && <p className="mt-3 text-xs text-red-700">{recoveryError}</p>}

            {isUploadIncomplete && (
              <button
                type="button"
                onClick={handleRecoverUploads}
                disabled={recoveringUploads}
                className="mt-3 rounded bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {recoveringUploads ? '再アップロード中...' : '不足ファイルをアップロードして開始'}
              </button>
            )}

            {canManualStart && (
              <button
                type="button"
                onClick={handleManualStart}
                disabled={recoveringUploads}
                className="mt-3 rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {recoveringUploads ? '開始中...' : '処理を開始'}
              </button>
            )}
          </div>
        )}

        {isSectionPending && sectionConfigUrl && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            <p className="font-semibold">セクション設定の入力が必要です</p>
            <p className="mt-1">文字起こしと Gemini 補正が完了しました。続けるにはセクション境界を設定してください。</p>
            <Link
              to={sectionConfigUrl}
              className="mt-3 inline-block rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              セクション設定へ進む →
            </Link>
          </div>
        )}

        {failed && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p className="font-semibold">
              エラーが発生しました{job.error ? `（${job.error.stage}）` : ''}
            </p>
            {job.error?.message && <p className="mt-1">{job.error.message}</p>}
            {job.error?.retriable !== false && (
              <p className="mt-2 text-xs text-red-600">※ このエラーはリトライ可能です。</p>
            )}
            {job.error?.retriable !== false && canRetry && (
              <button
                type="button"
                onClick={() => onRetry(job.job_id)}
                disabled={retrying}
                className="mt-3 rounded bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {retrying ? '再試行中...' : '再試行'}
              </button>
            )}
          </div>
        )}

        {isCompleted && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <p className="font-semibold">処理が完了しました</p>
            {job.result?.evaluation_summary && (
              <p className="mt-1">
                評価結果: {job.result.evaluation_summary.evaluation_result}
                {typeof job.result.evaluation_summary.issue_count === 'number' &&
                  ` ／ 指摘 ${job.result.evaluation_summary.issue_count} 件`}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
